"use client";

/**
 * WebSearchDisplay - Web search results with markdown rendering
 *
 * Displays search results from Perplexity/web search tools with:
 * - Rich markdown rendering via Streamdown
 * - Collapsible citations/sources list
 * - Loading animation
 * - Raw data toggle
 */

import { useState } from "react";
import { Card, Tag, Alert } from "@/ui";
import { colors, spacing, radius, textStyles, typography, primitives } from "@/ui/tokens";
import {
  Search,
  Language,
  Link as LinkIcon,
  Code,
  ExpandMore,
  ExpandLess,
  Info,
  SearchOff,
} from "@mui/icons-material";
import { Streamdown } from "streamdown";
import remarkGfm from "remark-gfm";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolDivider,
  ToolActions,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface WebSearchOutput {
  success?: boolean;
  query?: string;
  focus?: "concise" | "detailed" | "comprehensive";
  answer?: string;
  citations?: string[];
  metadata?: {
    searchedAt?: string;
    source?: string;
    realTimeData?: boolean;
    citationCount?: number;
  };
  error?: string;
  message?: string;
  fallbackApproach?: string;
}

const FOCUS_CONFIG = {
  concise: { label: "Concise", color: "neutral" as const },
  detailed: { label: "Detailed", color: "primary" as const },
  comprehensive: { label: "Comprehensive", color: "info" as const },
};

function CitationItem({ citation, index }: { citation: string; index: number }) {
  return (
    <Card
      variant="outlined"
      padding="sm"
      style={{
        borderRadius: radius.md,
        transition: "all 0.2s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: spacing[3] }}>
        <Tag
          color="primary"
          size="sm"
          style={{
            minWidth: 28,
            fontSize: `${typography.label.fontSize}px`,
            fontWeight: typography.fontWeight.medium,
          }}
        >
          {index + 1}
        </Tag>
        <div style={{ flex: 1, minWidth: 0 }}>
          <a
            href={citation}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...textStyles.body2,
              color: colors.action.product,
              textDecoration: "none",
              wordBreak: "break-all",
              display: "block",
            }}
          >
            {citation}
          </a>
        </div>
      </div>
    </Card>
  );
}

export function WebSearchDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showRawData, setShowRawData] = useState(false);
  const [showCitations, setShowCitations] = useState(false);

  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as WebSearchOutput | undefined;
  const input = toolCall.input as { query?: string; focus?: string } | undefined;

  const query = input?.query || result?.query || "Searching...";
  const focus = (input?.focus || result?.focus || "detailed") as keyof typeof FOCUS_CONFIG;
  const focusConfig = FOCUS_CONFIG[focus] || FOCUS_CONFIG.detailed;

  const answer = result?.answer || "";
  const citations = result?.citations || [];
  const metadata = result?.metadata;
  const isSuccess = result?.success !== false;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title="Web Search"
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Search style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {/* Query Info Bar */}
        <div
          style={{
            padding: `${spacing[4]}px ${spacing[5]}px`,
            backgroundColor: primitives.neutral[50],
            borderBottom: `1px solid ${colors.border.subtle}`,
          }}
        >
          <span
            style={{
              ...textStyles.body2,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              marginBottom: spacing[2],
            }}
          >
            {query}
          </span>
          <div style={{ display: "flex", gap: spacing[4], alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
              <Language style={{ fontSize: 20, color: colors.text.muted }} />
              <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                Real-time Web
              </span>
            </div>
            <Tag
              icon={<Info style={{ fontSize: 14 }} />}
              color={focusConfig.color}
              size="sm"
            >
              {focusConfig.label}
            </Tag>
          </div>
        </div>

        {/* Loading / Searching State */}
        {!answer && !isError && (isLoading || isStreaming) && (
          <div style={{ padding: spacing[6], textAlign: "center" }}>
            <ToolProgress
              variant="circular"
              label="Searching the web..."
            />
          </div>
        )}

        {/* Error State */}
        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
            {result?.fallbackApproach && (
              <Card
                variant="flat"
                padding="sm"
                style={{
                  borderRadius: radius.md,
                  backgroundColor: primitives.neutral[50],
                  marginTop: spacing[4],
                }}
              >
                <span style={{ ...textStyles.body2, fontWeight: 600, marginBottom: spacing[2], display: "block" }}>
                  Alternative Approach:
                </span>
                <span style={{ ...textStyles.body2, whiteSpace: "pre-line" }}>
                  {result.fallbackApproach}
                </span>
              </Card>
            )}
          </div>
        )}

        {/* Success State with Answer */}
        {!isLoading && !isError && answer && (
          <div style={{ padding: spacing[5] }}>
            {/* Markdown Content */}
            <div
              className="web-search-content"
              style={{
                /* Markdown styling is applied via CSS class */
              }}
            >
              <Streamdown remarkPlugins={[remarkGfm]}>{answer}</Streamdown>
            </div>
          </div>
        )}

        {/* Citations Section */}
        {!isLoading && citations.length > 0 && (
          <>
            <ToolDivider />
            <div style={{ backgroundColor: primitives.neutral[50] }}>
              <div
                onClick={() => setShowCitations(!showCitations)}
                style={{
                  padding: `${spacing[3]}px ${spacing[5]}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                  <LinkIcon style={{ fontSize: 20, color: colors.action.product }} />
                  <span style={{ ...textStyles.body2, fontWeight: 600 }}>
                    Sources & Citations ({citations.length})
                  </span>
                </div>
                <button
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    padding: spacing[1],
                    borderRadius: radius.sm,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.text.muted,
                  }}
                >
                  {showCitations ? <ExpandLess /> : <ExpandMore />}
                </button>
              </div>

              {showCitations && (
                <div style={{ padding: `0 ${spacing[5]}px ${spacing[4]}px` }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
                    {citations.map((citation, index) => (
                      <CitationItem key={index} citation={citation} index={index} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Metadata Footer */}
        {!isLoading && metadata && (
          <>
            <ToolDivider />
            <div
              style={{
                padding: `${spacing[3]}px ${spacing[5]}px`,
                backgroundColor: primitives.neutral[50],
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                {metadata.searchedAt
                  ? `Searched ${new Date(metadata.searchedAt).toLocaleString()}`
                  : "Searched recently"}
                {metadata.source && ` \u00B7 ${metadata.source}`}
                {metadata.realTimeData && " \u00B7 Real-time data"}
              </span>

              <ToolActions
                status={status}
                copyData={result}
                compact
                showRawToggle
                isRawView={showRawData}
                onToggleRaw={() => setShowRawData(!showRawData)}
              />
            </div>
          </>
        )}

        {/* Raw Data View */}
        {showRawData && result && (
          <div style={{ padding: `0 ${spacing[5]}px ${spacing[5]}px` }}>
            <Card
              variant="flat"
              padding="sm"
              style={{
                borderRadius: radius.md,
                backgroundColor: primitives.neutral[900],
                color: primitives.neutral[200],
                overflow: "auto",
                maxHeight: 400,
              }}
            >
              <pre style={{ margin: 0, fontSize: `${typography.scale.xs[0]}px`, fontFamily: typography.fontFamily.mono }}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </Card>
          </div>
        )}

        {/* Empty State - only show after streaming is done */}
        {!isStreaming && !isLoading && !isError && !answer && !result?.message && (
          <ToolEmptyState icon={<SearchOff />} message="No search results available" />
        )}

        {/* Message Only */}
        {!isLoading && !answer && result?.message && (
          <div style={{ padding: spacing[4] }}>
            <Alert variant={isSuccess ? "info" : "warning"} title={isSuccess ? "Info" : "Warning"}>
              {result.message}
            </Alert>
          </div>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default WebSearchDisplay;
